import { useCallback, useEffect, useRef } from "react"
import { captureError } from "@/shared/lib/diagnostics"
import { onAppActive } from "@/shared/lib/platform"
import { getDeviceTimezone } from "@/shared/lib/timezone"
import { useUpdateUser } from "../api/mutations"
import type { User } from "./types"

export function useSyncUserTimezone(user: User): void {
	const { mutateAsync: updateUser } = useUpdateUser()
	const userId = user.id
	const userTimezone = user.timezone
	const knownRef = useRef<string | null>(userTimezone)
	const pendingRef = useRef<string | null>(null)

	useEffect(() => {
		knownRef.current = userTimezone
		pendingRef.current = null
	}, [userTimezone])

	const syncTimezone = useCallback(async () => {
		const timezone = getDeviceTimezone()
		if (!timezone) return
		if (knownRef.current === timezone) return
		if (pendingRef.current === timezone) return

		pendingRef.current = timezone

		try {
			const updatedUser = await updateUser({ id: userId, timezone })
			knownRef.current = updatedUser.timezone ?? timezone
		} finally {
			if (pendingRef.current === timezone) {
				pendingRef.current = null
			}
		}
	}, [updateUser, userId])

	useEffect(() => {
		const sync = () => {
			void syncTimezone().catch((error) => {
				captureError(error, { scope: "user.syncTimezone" })
			})
		}
		sync()
		return onAppActive(sync)
	}, [syncTimezone])
}
